import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, message, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import TranscriptEditor, { normalizeTranscriptHtml } from './TranscriptEditor';
import VideoPlaybackPane from './VideoPlaybackPane';
import TrainingPdfViewer from './TrainingPdfViewer';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { DocumentProgress, TrainingDocument as DocumentData, TrainingFolder, TrainingVideo } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import { formatTrainingDate } from '../utils/trainingDates';
import { relatedTrainingMaterials, selectedTrainingMaterial, TrainingMaterials } from '../utils/trainingDocuments';
import '../pages/VideoPortal.css';
import './TrainingWorkspace.css';

const { Title, Paragraph, Text } = Typography;
interface TranscriptContent { text: string; html: string; revision: string }

export function relatedTrainingDocuments(primary: DocumentData, documents: DocumentData[]): DocumentData[] {
  const related = primary.training_date ? documents.filter(item => item.folder_id === primary.folder_id && item.training_date === primary.training_date) : [];
  return [primary, ...related.filter(item => item.id !== primary.id)];
}

const DocumentActions: React.FC<{ document: DocumentData; children?: React.ReactNode }> = ({ document, children }) => {
  const [progress, setProgress] = useState(document.progress);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    let active = true;
    api.post<DocumentProgress>(`/video/documents/${document.id}/opened`).then(({ data }) => {
      if (active) setProgress(current => current?.is_completed && !data.is_completed ? current : data);
    }).catch(() => { if (active) message.warning('Документ открыт, но отметку о просмотре сохранить не удалось'); });
    return () => { active = false; };
  }, [document.id]);
  const complete = async () => {
    setBusy(true);
    try { const { data } = await api.post<DocumentProgress>(`/video/documents/${document.id}/complete`); setProgress(data); }
    catch (err) { message.error(getApiErrorMessage(err, 'Не удалось сохранить отметку')); }
    finally { setBusy(false); }
  };
  const download = async () => {
    setDownloading(true);
    let url = '';
    try {
      const { data } = await api.get(`/video/documents/${document.id}/file`, { responseType: 'blob' });
      url = URL.createObjectURL(data);
      const anchor = window.document.createElement('a');
      anchor.href = url; anchor.download = document.original_filename;
      window.document.body.appendChild(anchor); anchor.click(); anchor.remove();
    } catch (err) { message.error(getApiErrorMessage(err, 'Не удалось скачать документ')); }
    finally { if (url) setTimeout(() => URL.revokeObjectURL(url), 1000); setDownloading(false); }
  };
  return <Space wrap size={[8, 8]} className="training-document-actions">
    <Button size="small" icon={<DownloadOutlined />} loading={downloading} onClick={download}>Скачать {document.document_kind === 'transcript' ? 'текст' : 'PDF'}</Button>
    {progress?.is_completed ? <Tag icon={<CheckCircleOutlined />} color="success">Прочитано</Tag> : <Button size="small" icon={<CheckCircleOutlined />} loading={busy} onClick={complete}>Отметить прочитанным</Button>}
    {children}
  </Space>;
};

const PresentationPane: React.FC<{ document: DocumentData }> = ({ document }) => {
  return <>
    <DocumentActions document={document} />
    <TrainingPdfViewer documentId={document.id} title={document.title} />
  </>;
};

const TranscriptPane: React.FC<{ document: DocumentData; initialEdit: boolean; onEditing: (value: boolean) => void }> = ({ document, initialEdit, onEditing }) => {
  const { isAdmin } = useAuth();
  const [content, setContent] = useState<TranscriptContent | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(initialEdit && isAdmin);
  useEffect(() => { onEditing(editing); return () => onEditing(false); }, [editing, onEditing]);
  useEffect(() => {
    const controller = new AbortController();
    api.get<TranscriptContent>(`/video/documents/${document.id}/text`, { signal: controller.signal }).then(({ data }) => {
      if (!controller.signal.aborted) setContent({ ...data, html: normalizeTranscriptHtml(data.html) });
    }).catch(err => { if (!controller.signal.aborted) { setError(getApiErrorMessage(err, 'Не удалось открыть текст')); setEditing(false); } });
    return () => controller.abort();
  }, [document.id]);
  useEffect(() => {
    if (!editing) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editing]);
  const save = async (html: string) => {
    if (!content) return;
    try {
      const { data } = await api.patch<TranscriptContent>(`/video/admin/documents/${document.id}/text`, { html, expected_revision: content.revision });
      setContent({ ...data, html: normalizeTranscriptHtml(data.html) }); setEditing(false); message.success('Текст сохранён');
    } catch (err) { throw new Error(getApiErrorMessage(err, 'Не удалось сохранить текст. Изменения остаются в редакторе.')); }
  };
  return <>
    <DocumentActions document={document}>
      {isAdmin && content && !error && !editing && <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(true)}>Редактировать текст</Button>}
    </DocumentActions>
    {error ? <Alert type="error" showIcon message={error} /> : !content ? <Skeleton active /> : editing ? <TranscriptEditor html={content.html} onSave={save} onCancel={() => setEditing(false)} /> : <>
      <div className="training-transcript-html training-transcript-view" dangerouslySetInnerHTML={{ __html: content.html }} />
    </>}
  </>;
};

type MaterialKind = 'video' | 'presentation' | 'transcript';

const TrainingWorkspace: React.FC<{ primaryKind: 'video' | 'document'; primaryId?: string }> = ({ primaryKind, primaryId }) => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [primary, setPrimary] = useState<TrainingVideo | DocumentData | null>(null);
  const [materials, setMaterials] = useState<TrainingMaterials>({ videos: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pairError, setPairError] = useState('');
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setPairError(''); setPrimary(null);
    setMaterials({ videos: [], documents: [] }); setEditing(false);
    const load = async () => {
      try {
        const endpoint = primaryKind === 'video' ? 'videos' : 'documents';
        const { data } = await api.get<TrainingVideo | DocumentData>(`/video/${endpoint}/${primaryId}`, { signal: controller.signal });
        let folder: TrainingFolder | null = null;
        try {
          const response = await api.get<TrainingFolder>(`/video/folders/${data.folder_id}`, { signal: controller.signal });
          folder = response.data;
        } catch (err) {
          if (!controller.signal.aborted) setPairError('Не удалось загрузить связанные материалы. Открыт выбранный материал.');
        }
        if (controller.signal.aborted) return;
        setPrimary(data);
        setMaterials(relatedTrainingMaterials(data, primaryKind, folder?.videos || [], folder?.documents || []));
      } catch (err) { if (!controller.signal.aborted) setError(getApiErrorMessage(err, 'Не удалось открыть обучение')); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    };
    load();
    return () => controller.abort();
  }, [primaryKind, primaryId]);

  const presentations = materials.documents.filter(item => item.document_kind === 'presentation');
  const transcripts = materials.documents.filter(item => item.document_kind === 'transcript');
  const primaryMaterialKind: MaterialKind = primaryKind === 'video' ? 'video' : (primary as DocumentData | null)?.document_kind || 'presentation';
  const video = selectedTrainingMaterial(materials.videos, params.get('video'), primaryMaterialKind === 'video' ? primary?.id : undefined);
  const presentation = selectedTrainingMaterial(presentations, params.get('presentation'), primaryMaterialKind === 'presentation' ? primary?.id : undefined);
  const transcript = selectedTrainingMaterial(transcripts, params.get('transcript'), primaryMaterialKind === 'transcript' ? primary?.id : undefined);
  const candidates = { video: materials.videos, presentation: presentations, transcript: transcripts };
  const invalidLink = !loading && primary && (['video', 'presentation', 'transcript'] as MaterialKind[]).some(kind => {
    const value = params.get(kind);
    return value !== null && (!/^[1-9]\d*$/.test(value) || !candidates[kind].some(item => item.id === Number(value)));
  });
  const choose = (kind: MaterialKind, id: number) => {
    const next = new URLSearchParams(params);
    if (kind === primaryMaterialKind) {
      next.delete(kind);
      const route = kind === 'video' ? `/videos/${id}` : `/videos/documents/${id}`;
      navigate(route + (next.toString() ? `?${next}` : ''));
    } else {
      next.set(kind, String(id));
      setParams(next, { replace: true });
    }
  };
  const selector = (kind: MaterialKind, value: number | undefined, label: string) => {
    const items = candidates[kind];
    return (items.length > 1 || (items.length > 0 && value === undefined)) && <Select
      aria-label={label} placeholder={label} disabled={editing} value={value}
      onChange={(id: number) => choose(kind, id)} className="training-workspace-selector"
      options={items.map(item => ({ value: item.id, label: item.title }))} />;
  };

  return <Layout wide><div className="training-workspace">
    <Button className="video-back" disabled={editing} icon={<ArrowLeftOutlined />} onClick={() => navigate(primary ? `/videos/folders/${primary.folder_id}` : '/videos')}>К папке обучения</Button>
    {error && <Alert className="video-alert" type="error" showIcon message={error} />}
    {pairError && <Alert className="video-alert" type="warning" showIcon message={pairError} />}
    {invalidLink && <Alert className="video-alert" type="warning" showIcon message="В ссылке указан материал, который не относится к этому обучению. Выберите материал из списка." />}
    {loading ? <Card><Skeleton active /></Card> : primary && <>
      <div className="video-page-head"><div>
        <Text className="video-eyebrow">МАТЕРИАЛЫ ОБУЧЕНИЯ</Text>
        <Title level={2}>{primary.training_date ? `Обучение ${formatTrainingDate(primary.training_date)}` : primary.title}</Title>
        <Paragraph type="secondary">{primary.training_date ? `Дата обучения: ${formatTrainingDate(primary.training_date)}` : 'Дата обучения не указана'}</Paragraph>
      </div></div>
      <div className={`training-workspace-content ${materials.videos.length > 0 ? 'training-workspace-has-video' : ''} ${presentations.length > 0 || transcripts.length > 0 ? 'training-workspace-has-documents' : ''} ${presentations.length > 0 && transcripts.length > 0 ? 'training-workspace-three-panes' : ''}`}>
      {materials.videos.length > 0 && <Card className="training-workspace-video" title="Видеозапись">
        {selector('video', video?.id, 'Выбор видеозаписи')}
        {video ? <VideoPlaybackPane key={video.id} video={video} /> : <Paragraph type="secondary">Выберите видеозапись этого обучения.</Paragraph>}
      </Card>}
      <div className={`training-workspace-documents ${presentations.length > 0 && transcripts.length > 0 ? 'training-workspace-documents-paired' : ''}`}>
        {presentations.length > 0 && <Card className="training-material-panel" title="Презентация">
          {selector('presentation', presentation?.id, 'Выбор презентации')}
          {presentation ? <PresentationPane key={presentation.id} document={presentation} /> : <Paragraph type="secondary">Выберите презентацию этого обучения.</Paragraph>}
        </Card>}
        {transcripts.length > 0 && <Card className="training-material-panel" title="Текст к презентации">
          {selector('transcript', transcript?.id, 'Выбор транскрибации')}
          {transcript ? <TranscriptPane key={transcript.id} document={transcript} initialEdit={params.get('edit') === '1'} onEditing={setEditing} /> : <Paragraph type="secondary">Выберите текст этого обучения.</Paragraph>}
        </Card>}
      </div>
      </div>
      {materials.videos.length === 0 && <Paragraph className="training-workspace-note" type="secondary">Видеозапись с этой датой в папке пока не добавлена.</Paragraph>}
      {presentations.length === 0 && <Paragraph className="training-workspace-note" type="secondary">Презентация с этой датой в папке пока не добавлена.</Paragraph>}
      {transcripts.length === 0 && <Paragraph className="training-workspace-note" type="secondary">Транскрибация с этой датой в папке пока не добавлена.</Paragraph>}
    </>}
  </div></Layout>;
};

export default TrainingWorkspace;
